import cx from 'clsx';

import classes from './Shimmer.module.css';

export interface ShimmerProps {
  variant?: 'text' | 'background';
  speed?: number;
  spread?: number | string;
  angle?: number;
  color?: string;
  duration?: number;
  repeatDelay?: number;
  trackWidth?: number | string;
  trackHeight?: number | string;
  isContainer?: boolean;
  isInverted?: boolean;
  className?: string;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}

function formatCssLength(value: number | string): string {
  if (typeof value === 'number') {
    return `${value}px`;
  }
  return value;
}

/**
 * Compute shimmer sweep duration in ms from spatial parameters.
 *
 * CSS calc() cannot convert <length> to <time> (dividing px/px is invalid),
 * so this must happen in JS. Values with CSS-only units (ch, cqw, %) are
 * approximated using the same pixel defaults the CSS fallbacks use.
 */
function computeShimmerDuration(
  variant: 'text' | 'background',
  speed?: number,
  spread?: number | string,
  angle?: number,
  trackWidth?: number | string,
  trackHeight?: number | string,
): number {
  const isBackground = variant === 'background';
  const effectiveSpeed = speed ?? (isBackground ? 1000 : 400);
  const angleDeg = angle ?? 15;
  const angleRad = (angleDeg * Math.PI) / 180;

  const toPx = (value: number | string | undefined, fallback: number) => (typeof value === 'number' ? value : fallback);

  // Approximate spread — text default is calc(4ch + 80px) ≈ 112px at ~8px/ch
  const spreadPx = toPx(spread, isBackground ? 480 : 112);
  const trackHeightPx = toPx(trackHeight, 200);
  const trackWidthPx = toPx(trackWidth, 200);

  const gradientWidth = spreadPx + trackHeightPx * Math.tan(angleRad);
  const activeDistance = trackWidthPx + gradientWidth;

  return (activeDistance / effectiveSpeed) * 1000;
}

/** Animated shimmer effect for text or background surfaces. */
export function Shimmer({
  variant = 'text',
  speed,
  spread,
  angle,
  color,
  duration,
  repeatDelay,
  trackWidth,
  trackHeight,
  isContainer = false,
  isInverted = false,
  className,
  style,
  children,
}: ShimmerProps) {
  const customProperties: Record<string, string | number> = {};

  // Duration MUST be computed in JS — CSS calc() cannot convert <length> to <time>
  customProperties['--shimmer-duration'] =
    duration ?? computeShimmerDuration(variant, speed, spread, angle, trackWidth, trackHeight);

  if (speed !== undefined) {
    customProperties['--shimmer-speed'] = speed;
  }
  if (spread !== undefined) {
    customProperties['--shimmer-spread'] = formatCssLength(spread);
  }
  if (angle !== undefined) {
    customProperties['--shimmer-angle'] = `${angle}deg`;
  }
  if (color !== undefined) {
    customProperties['--shimmer-color'] = color;
  }
  if (repeatDelay !== undefined) {
    customProperties['--shimmer-repeat-delay'] = repeatDelay;
  }
  if (trackWidth !== undefined) {
    customProperties['--shimmer-track-width'] = formatCssLength(trackWidth);
  }
  if (trackHeight !== undefined) {
    customProperties['--shimmer-track-height'] = formatCssLength(trackHeight);
  }

  const mergedStyle = { ...customProperties, ...style } as React.CSSProperties;

  const isBackground = variant === 'background';

  if (isBackground) {
    return (
      <div
        className={cx(classes.shimmer, classes.shimmerBackground, isContainer && classes.shimmerContainer, className)}
        style={mergedStyle}
      >
        {children}
      </div>
    );
  }

  return (
    <span
      className={cx(
        classes.shimmer,
        isInverted && classes.shimmerInverted,
        isContainer && classes.shimmerContainer,
        className,
      )}
      style={mergedStyle}
    >
      {children}
    </span>
  );
}
